import React from 'react';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import SearchIcon from '@material-ui/icons/Search';

import Config from '../../constants/appConfig';

const jobDate = (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '-';
const normalizeSearch = (value) => String(value || '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const jobKey = (job) => String(job.id_job_link || `${job.id_job}-${job.guid}`);

class ImportJobsDialog extends React.Component {
  fileInput = React.createRef();

  state = {
    files: [], companies: [], sourceCompany: '', jobs: [], selectedJobs: [],
    companiesLoading: false, jobsLoading: false, importing: false,
    dragActive: false, error: '', success: '', jobSearch: '',
    companySearch: '', companyMenuOpen: false
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      this.setState({
        files: [], sourceCompany: '', jobs: [], selectedJobs: [], jobSearch: '',
        companySearch: '', companyMenuOpen: false,
        importing: false, dragActive: false, error: '', success: ''
      });
      this.loadCompanies();
    }
  }

  authHeaders = () => ({ Authorization: localStorage.getItem('access-token-jwt') });

  readResponse = (response) => {
    if (!response.ok) throw response;
    return response.json();
  };

  handleRequestError = (error, message) => {
    if (error && (error.status === 401 || error.status === 403)) {
      this.props.link('/login');
      return;
    }
    this.setState({ error: message });
  };

  loadCompanies = () => {
    this.setState({ companiesLoading: true, error: '' });
    fetch(`${Config.server}/job_view/import_companies/all`, { headers: this.authHeaders() })
      .then(this.readResponse)
      .then((companies) => this.setState({
        companies: Array.isArray(companies) ? companies : [], companiesLoading: false
      }))
      .catch((error) => {
        this.setState({ companiesLoading: false });
        this.handleRequestError(error, 'Nao foi possivel carregar as empresas disponiveis.');
      });
  };

  selectCompany = (event, company) => {
    const sourceCompany = company ? company.id : '';
    this.setState({
      sourceCompany, jobs: [], selectedJobs: [], jobSearch: '',
      companySearch: company ? company.name : '', companyMenuOpen: false,
      jobsLoading: Boolean(sourceCompany), error: ''
    });
    if (!sourceCompany) return;

    fetch(`${Config.server}/job_view/import_jobs/${sourceCompany}`, { headers: this.authHeaders() })
      .then(this.readResponse)
      .then((jobs) => {
        const filteredJobs = Array.isArray(jobs) ? jobs.reduce((accumulator, current) => {
          const isDuplicate = accumulator.some(item => item.id_job === current.id_job);
          if (!isDuplicate) {
            accumulator.push(current);
          }
          return accumulator;
        }, []) : [];

        this.setState({
          jobs: filteredJobs, jobsLoading: false
        });
      })
      .catch((error) => {
        this.setState({ jobsLoading: false });
        this.handleRequestError(error, 'Nao foi possivel carregar os trabalhos da empresa.');
      });
  };

  addFiles = (fileList) => {
    const received = Array.from(fileList || []);
    const supported = received.filter((file) => file.name.toLowerCase().endsWith('.zjob'));
    const rejected = received.length - supported.length;
    this.setState((state) => {
      const files = state.files.slice();
      supported.forEach((file) => {
        const exists = files.some((item) => item.name === file.name
          && item.size === file.size && item.lastModified === file.lastModified);
        if (!exists) files.push(file);
      });
      return {
        files,
        error: rejected ? 'Somente arquivos .zjob podem ser importados.' : '',
        success: ''
      };
    });
  };

  chooseFiles = (event) => {
    this.addFiles(event.target.files);
    event.target.value = null;
  };

  dropFiles = (event) => {
    event.preventDefault();
    this.setState({ dragActive: false });
    this.addFiles(event.dataTransfer.files);
  };

  toggleJob = (job) => {
    const key = jobKey(job);
    this.setState((state) => ({
      selectedJobs: state.selectedJobs.includes(key)
        ? state.selectedJobs.filter((item) => item !== key)
        : state.selectedJobs.concat(key),
      success: ''
    }));
  };

  getVisibleJobs = () => {
    const { jobs, selectedJobs, jobSearch } = this.state;
    const search = normalizeSearch(jobSearch);
    return jobs.filter((job) => {
      if (selectedJobs.includes(jobKey(job)) || !search) return true;
      return normalizeSearch([
        job.title, job.id_job, job.plane_name, job.client_name, job.type,
        jobDate(job.datetime || job.created)
      ].join(' ')).includes(search);
    }).sort((first, second) => {
      const firstSelected = selectedJobs.includes(jobKey(first));
      const secondSelected = selectedJobs.includes(jobKey(second));
      if (firstSelected !== secondSelected) return firstSelected ? -1 : 1;
      return 0;
    });
  };

  getVisibleCompanies = () => {
    const search = normalizeSearch(this.state.companySearch);
    if (!search) return this.state.companies;
    return this.state.companies.filter((company) => normalizeSearch(company.name).includes(search));
  };

  changeCompanySearch = (event) => {
    const companySearch = event.target.value;
    this.setState({
      companySearch,
      companyMenuOpen: true,
      sourceCompany: '',
      jobs: [],
      selectedJobs: [],
      jobSearch: ''
    });
  };

  uploadFile = (file) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return fetch(`${Config.server}/job_view`, {
      method: 'POST', body: formData, headers: this.authHeaders()
    }).then(this.readResponse);
  };

  importJobs = () => {
    const { files, sourceCompany, selectedJobs, jobs } = this.state;
    const startedAt = Date.now();
    const pendingItems = files.map((file, index) => ({
      key: `file-${startedAt}-${index}`,
      title: file.name.replace(/\.zjob$/i, '') || file.name,
      created: startedAt
    })).concat(selectedJobs.map((key, index) => {
      const job = jobs.find((item) => jobKey(item) === key);
      return {
        key: `system-${startedAt}-${index}`,
        title: job && job.title ? job.title : `Trabalho importado ${index + 1}`,
        created: startedAt
      };
    }));
    const requests = files.map(this.uploadFile);
    if (sourceCompany && selectedJobs.length) {
      const selectedItems = selectedJobs.map((key) => {
        const job = jobs.find((item) => jobKey(item) === key);
        return job ? { id_job_link: job.id_job_link, guid: job.guid } : null;
      }).filter(Boolean);
      requests.push(fetch(`${Config.server}/job_view`, {
        method: 'PUT',
        headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_company: sourceCompany, list: selectedItems })
      }).then(this.readResponse));
    }
    if (!requests.length) return;

    this.setState({ importing: true, error: '', success: '' });
    Promise.all(requests)
      .then((responses) => {
        let startedTasks = responses.slice(0, files.length);
        if (sourceCompany && selectedJobs.length) {
          const systemResponse = responses[files.length];
          startedTasks = startedTasks.concat(
            systemResponse && Array.isArray(systemResponse.tasks) ? systemResponse.tasks : []
          );
        }
        const trackedItems = pendingItems.map((item, index) => {
          const task = startedTasks[index] || {};
          return {
            ...item,
            id_task: task.id_task || null,
            title: task.title || item.title,
            progress: Number(task.progress) || 0,
            status: task.id_task ? (task.status || 'processing') : 'canceled'
          };
        });

        this.setState({
          importing: false, files: [], selectedJobs: [], success: ''
        });
        if (this.props.onImported) this.props.onImported(trackedItems);
        if (this.props.onClose) this.props.onClose();
      })
      .catch((error) => {
        this.setState({ importing: false });
        this.handleRequestError(error, 'Nao foi possivel iniciar a importacao. Verifique os arquivos selecionados.');
      });
  };

  render() {
    const { open, onClose } = this.props;
    const {
      files, sourceCompany, selectedJobs, companiesLoading,
      jobsLoading, importing, dragActive, error, success, jobSearch,
      companySearch, companyMenuOpen
    } = this.state;
    const canImport = files.length > 0 || selectedJobs.length > 0;
    const visibleJobs = this.getVisibleJobs();
    const visibleCompanies = this.getVisibleCompanies();

    return <Dialog
      open={open} onClose={importing ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ className: 'job-yield-import-dialog' }}
    >
      <DialogTitle className="job-yield-import-title" disableTypography>
        <div><CloudUploadOutlinedIcon /></div>
        <div><strong>Importar novos trabalhos</strong><span>Envie arquivos ou selecione trabalhos existentes no sistema</span></div>
      </DialogTitle>
      <DialogContent className="job-yield-import-content">
        <section className="job-yield-import-section">
          <div className="job-yield-import-section-title">
            <CloudUploadOutlinedIcon />
            <div><strong>Arquivo local</strong><span>Escolha em uma pasta ou arraste para a area abaixo</span></div>
          </div>
          <input ref={this.fileInput} hidden multiple type="file" accept=".zjob" onChange={this.chooseFiles} />
          <div
            className={`job-yield-dropzone ${dragActive ? 'active' : ''}`}
            onClick={() => this.fileInput.current && this.fileInput.current.click()}
            onDragEnter={(event) => { event.preventDefault(); this.setState({ dragActive: true }); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              event.preventDefault();
              if (!event.currentTarget.contains(event.relatedTarget)) this.setState({ dragActive: false });
            }}
            onDrop={this.dropFiles}
          >
            <CloudUploadOutlinedIcon />
            <strong>Arraste arquivos .zjob aqui</strong>
            <span>ou clique para selecionar em uma pasta</span>
          </div>
          {files.length > 0 && <div className="job-yield-selected-files">
            {files.map((file) => <div key={`${file.name}-${file.lastModified}`}>
              <DescriptionOutlinedIcon /><span>{file.name}</span>
              <button type="button" onClick={() => this.setState({ files: files.filter((item) => item !== file) })}>Remover</button>
            </div>)}
          </div>}
        </section>

        <div className="job-yield-import-or"><span>ou</span></div>

        <section className="job-yield-import-section">
          <div className="job-yield-import-section-title">
            <BusinessOutlinedIcon />
            <div><strong>Trabalho existente no sistema</strong><span>Selecione a empresa e os trabalhos que deseja processar</span></div>
          </div>
          <div className="job-yield-company-picker">
            <TextField
              variant="outlined" size="small" fullWidth label="Empresa"
              placeholder="Digite para buscar uma empresa"
              value={companySearch}
              disabled={companiesLoading || importing}
              onFocus={() => this.setState({ companyMenuOpen: true })}
              onBlur={() => window.setTimeout(() => this.setState({ companyMenuOpen: false }), 120)}
              onChange={this.changeCompanySearch}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && visibleCompanies.length) {
                  event.preventDefault();
                  this.selectCompany(event, visibleCompanies[0]);
                }
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: companiesLoading ? <CircularProgress size={18} /> : null
              }}
            />
            {companyMenuOpen && !companiesLoading && <div className="job-yield-company-options" role="listbox">
              {visibleCompanies.map((company) => <button
                type="button" role="option" key={company.id}
                aria-selected={String(company.id) === String(sourceCompany)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => this.selectCompany(event, company)}
              >{company.name}</button>)}
              {!visibleCompanies.length && <div>Nenhuma empresa encontrada</div>}
            </div>}
          </div>
          {companiesLoading && <div className="job-yield-import-loading"><CircularProgress size={24} /></div>}
          {sourceCompany && !companiesLoading && <React.Fragment>
            <TextField
              className="job-yield-job-search"
              variant="outlined" size="small" fullWidth
              value={jobSearch}
              onChange={(event) => this.setState({ jobSearch: event.target.value })}
              placeholder="Digite para buscar por trabalho, aeronave, cliente ou data"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              }}
            />
            <div className="job-yield-system-jobs">
            {jobsLoading && <div className="job-yield-import-loading"><CircularProgress size={24} /></div>}
            {!jobsLoading && visibleJobs.map((job) => <label key={jobKey(job)} className={`job-yield-system-job ${selectedJobs.includes(jobKey(job)) ? 'selected' : ''}`}>
              <Checkbox
                color="primary" checked={selectedJobs.includes(jobKey(job))}
                onChange={() => this.toggleJob(job)}
              />
              <div><strong>{job.title || `Trabalho #${job.id_job}`}</strong>
                <span>{jobDate(job.datetime || job.created)} · {job.plane_name || 'Aeronave nao informada'} · {job.type}</span>
              </div>
            </label>)}
            {!jobsLoading && !visibleJobs.length && <div className="job-yield-import-empty">Nenhum trabalho encontrado para esta busca.</div>}
            </div>
          </React.Fragment>}
        </section>
        {error && <div className="job-yield-import-message error">{error}</div>}
        {success && <div className="job-yield-import-message success">{success}</div>}
      </DialogContent>
      <DialogActions className="job-yield-import-actions">
        <Button className="job-yield-import-close" onClick={onClose} disabled={importing}>Cancelar</Button>
        <Button
          variant="contained" className="job-yield-import-submit"
          startIcon={importing ? <CircularProgress size={18} /> : <CloudUploadOutlinedIcon />}
          disabled={!canImport || importing} onClick={this.importJobs}
        >{importing ? 'Iniciando...' : 'Gerar trabalhos'}</Button>
      </DialogActions>
    </Dialog>;
  }
}

export default ImportJobsDialog;
